import React, { useState, useEffect } from "react";
import axios from "axios";
import { BellOutlined } from "@ant-design/icons";
import { Badge, List, Popover, Button, message } from "antd";
import io from "socket.io-client";

const Notifications = ({ dbCode }) => {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const apiBaseUrl = process.env.REACT_APP_API_BASE_URL;

  const fetchNotifications = async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${apiBaseUrl}/common/notifications/${dbCode}`,
        {
          params: {
            limit: 5,
            unreadOnly: true
          }
        }
      );
      setNotifications(response.data);
      setUnreadCount(response.data.filter((n) => !n.IsRead).length);
    } catch (error) {
      console.error("Error fetching notifications:", error);
      message.error("Failed to load notifications");
    } finally {
      setLoading(false);
    }
  };

  const markAsRead = async (notificationId) => {
    try {
      await axios.put(
        `${apiBaseUrl}/common/notifications/mark-read/${dbCode}/${notificationId}`
      );
      fetchNotifications(); // Refresh notifications
    } catch (error) {
      console.error("Error marking notification as read:", error);
      message.error("Failed to mark notification as read");
    }
  };

  const markAllAsRead = async () => {
    try {
      await Promise.all(
        notifications
          .filter((n) => !n.IsRead)
          .map((n) =>
            axios.put(
              `${apiBaseUrl}/common/notifications/mark-read/${dbCode}/${n.NotificationId}`
            )
          )
      );
      fetchNotifications(); // Refresh notifications
      message.success("All notifications marked as read");
    } catch (error) {
      console.error("Error marking all notifications as read:", error);
      message.error("Failed to mark all notifications as read");
    }
  };

  useEffect(() => {
    fetchNotifications(); // Initial fetch
    
    // Connect to the WebSocket server
    const socket = io(apiBaseUrl);
    
    socket.on("NotificationUpdated", () => {
      //////console.log("Notification update received");
      fetchNotifications(); // Re-fetch when an update is detected
    });

    return () => {
      socket.disconnect(); // Clean up on unmount
    };
  }, [apiBaseUrl, dbCode]);

  const notificationContent = (
    <div style={{ width: 350 }}>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          padding: "8px 12px",
          borderBottom: "1px solid #f0f0f0",
        }}
      >
        <strong>Notifications</strong>
        {unreadCount > 0 && (
          <Button type="link" size="small" onClick={markAllAsRead}>
            Mark all as read
          </Button>
        )}
      </div>
      <List
        itemLayout="horizontal"
        dataSource={notifications}
        loading={loading}
        renderItem={(item) => (
          <List.Item
            style={{
              padding: "12px",
              backgroundColor: item.IsRead ? "#fff" : "#f6f6f6",
              cursor: "pointer",
            }}
            onClick={() => markAsRead(item.NotificationId)}
          >
            <List.Item.Meta
              title={
                <div style={{ fontWeight: item.IsRead ? "normal" : "bold" }}>
                  {item.Title}
                </div>
              }
              description={item.Message}
            />
            <div style={{ fontSize: 12, color: "#888" }}>
              {new Date(item.CreatedDate).toLocaleTimeString()}
            </div>
          </List.Item>
        )}
      />
    </div>
  );

  return (
    <Popover
      content={notificationContent}
      trigger="click"
      placement="bottomRight"
      overlayStyle={{ padding: 0 }}
      onOpenChange={(visible) => visible && fetchNotifications()}
    >
      <Badge count={unreadCount} overflowCount={9}>
        <BellOutlined style={{ fontSize: 20, cursor: "pointer" }} />
      </Badge>
    </Popover>
  );
};

export default Notifications;